import { useMutation, useQuery } from "@apollo/client";
import {
  Text,
  media,
  Button as DefaultButton,
  pxToRem,
  modularScale,
} from "@ottatech/design";
import { Loading } from "@ottatech/shared-components";
import styled from "styled-components";
import { useState } from "react";

import { IDashboardBannerNotification } from "..";

import {
  Notification,
  CurrentUserDocument,
  DismissNotificationDocument,
  UpdateUserPreferencesDocument,
} from "schema";
import { handleMutationError } from "utils/error";

const NotificationWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: ${modularScale()};
  align-items: center;
  ${media({ to: "tablet" })`
    grid-template-columns: 1fr;
  `}
`;

const Button = styled(DefaultButton)`
  width: ${pxToRem(64)};

  margin-right: 0.25rem;

  ${media({ to: "tablet" })`
    grid-template-columns: 1fr;
    width: 100%;
  `}
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const Submitted = () => {
  return <Text>Your preferences have been updated!</Text>;
};

const NeedEuVisa: React.FC = () => {
  const { data, loading } = useQuery(CurrentUserDocument);

  const [updateUserPreferences, { loading: mutationLoading }] = useMutation(
    UpdateUserPreferencesDocument
  );

  const [dismissMutation] = useMutation(DismissNotificationDocument, {
    onError: handleMutationError,
  });

  const [submitted, setSubmitted] = useState(false);

  const firstName = data?.currentUser?.firstName;

  const handleResponse = (response: boolean) => async () => {
    if (response) {
      await updateUserPreferences({
        variables: {
          input: { visaRequirementCountries: [{ location: "EU" }] },
        },
      });
    } else {
      await dismissMutation({
        variables: { notificationName: "NEED_EU_VISA" },
      });
    }
    setSubmitted(true);
  };

  if (loading || mutationLoading) {
    return <Loading />;
  }

  if (submitted) {
    return <Submitted />;
  }

  return (
    <NotificationWrapper data-testid="need-eu-visa">
      <Text>{firstName}, do you require a visa to work in the EU?</Text>
      <ButtonWrapper>
        <Button
          size="small"
          level="tertiary"
          data-analytics-id="notification-eu-visa-yes"
          onClick={handleResponse(true)}
          disabled={mutationLoading}
        >
          Yes
        </Button>
        <Button
          size="small"
          level="tertiary"
          data-analytics-id="notification-eu-visa-no"
          onClick={handleResponse(false)}
          disabled={mutationLoading}
        >
          No
        </Button>
      </ButtonWrapper>
    </NotificationWrapper>
  );
};

export const NeedEuVisaNotification: IDashboardBannerNotification = {
  key: "need-eu-visa-notification",
  name: Notification.NeedEuVisa,
  Content: NeedEuVisa,
  level: "positive",
  dismissable: false,
};
